import {
  Card,
  Box,
  Typography,
  Modal,
  Stack,
  Divider,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useGetUserAlertQuery,
  useListStrategiesQuery,
} from "features/trading/accounts";
import type { Strategy } from "features/trading/accounts";
import type { GridModel } from "features/api";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import {
  AlertLeg,
  AlertPayload,
  AlertType,
  asText,
  contractLabel,
  formatMoney,
} from "helpers/types";
import { updateLastStrategy, updateLastStrategyGroupId } from "helpers/index";
import {
  containerBorderSx,
  leftColDividerSx,
  tradeTypeChipSx,
  grayChipSx,
  actionCallChipSx,
  actionPutChipSx,
  greenMoneyChipSx,
  legRowSx,
} from "styles/RedirectionModal.styles";
import {
  NOTISTACK_FAILURE_OPTIONS,
  StrategyGroupEnum,
  StrategyGroupLabel,
} from "constants/index";
import { enqueueSnackbar } from "notistack";

export const RedirectionModal = ({
  open,
  onClose,
  alertId,
}: {
  open: boolean;
  onClose: () => void;
  alertId?: number;
}) => {
  const { data: alertData, isFetching } = useGetUserAlertQuery(alertId, {
    skip: !open || !alertId,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: false,
  });

  const alert = alertData as unknown as AlertType | undefined;

  const legs = useMemo<AlertLeg[]>(() => {
    const fromTop = (alert?.legs ?? []) as AlertLeg[];
    if (fromTop.length > 0) return fromTop;
    const fromPayload =
      (alert?.payload as AlertPayload | undefined)?.contract?.legs ?? [];
    return fromPayload ?? [];
  }, [alert]);

  const [selectedStrategyId, setSelectedStrategyId] = useState<number | "">("");

  const strategyFilters = useMemo(() => {
    const filters: Record<string, any> = {};
    if (alert?.symbol) filters["name"] = alert.symbol;
    if (alert?.contract_type) {
      if (alert?.contract_type === "C") filters["call_or_put"] = true;
      else if (alert?.contract_type === "P") filters["call_or_put"] = false;
      else filters["call_or_put"] = null;
    }
    return filters;
  }, [alert?.symbol, alert?.contract_type]);

  const strategiesParams = useMemo<GridModel>(
    () => ({
      pagination: { page: 0, pageSize: 50 },
      filters: strategyFilters,
    }),
    [strategyFilters],
  );

  const { data: strategiesResp, isFetching: strategiesLoading } =
    useListStrategiesQuery(strategiesParams, { skip: !open || !alert });

  const navigate = useNavigate();

  useEffect(() => {
    if (strategiesResp?.results?.length) {
      setSelectedStrategyId(strategiesResp.results[0].id);
    }
  }, [strategiesResp, alertId]);

  const selectedStrategy = useMemo(() => {
    if (!selectedStrategyId || !strategiesResp?.results) return null;
    return (
      strategiesResp.results.find(
        (s: Strategy) => s.id === selectedStrategyId,
      ) || null
    );
  }, [selectedStrategyId, strategiesResp?.results]);

  const strategiesList = useMemo(() => {
    return strategiesResp?.results || [];
  }, [strategiesResp?.results]);

  const isButtonDisabled = useMemo(
    () =>
      !selectedStrategyId ||
      !legs.length ||
      strategiesLoading ||
      !selectedStrategy,
    [selectedStrategyId, legs.length, strategiesLoading, selectedStrategy],
  );

  const contractTypeLabel = useMemo(
    () => contractLabel(alert?.contract_type),
    [alert?.contract_type],
  );

  const contractTypeChipSx = useMemo(
    () => (alert?.contract_type === "C" ? actionCallChipSx : actionPutChipSx),
    [alert?.contract_type],
  );

  const handleCopy = useCallback((text?: string | null) => {
    if (!text) return;
    navigator.clipboard?.writeText(text).catch(() => undefined);
  }, []);

  const handleCopyUid = useCallback(() => {
    if (alert?.order_id) {
      handleCopy(alert.order_id);
    }
  }, [alert?.order_id, handleCopy]);

  const handleSelectStrategy = useCallback((value: number | string) => {
    setSelectedStrategyId(value as number);
  }, []);

  const handleRedirect = (legs: AlertLeg[]) => {
    const params = new URLSearchParams();
    if (!selectedStrategy || !legs.length) return;

    legs.forEach((leg, idx) => {
      params.append(`strategy_id`, String(selectedStrategy.id));
      params.append(
        `strategy_group_id`,
        String(selectedStrategy.strategy_group),
      );
      if (leg.action) params.append(`legs[${idx}].action`, leg.action);
      if (leg.strike_price !== null && leg.strike_price !== undefined) {
        params.append(`legs[${idx}].strike_price`, String(leg.strike_price));
      }
      if (leg.expiration_date) {
        params.append(`legs[${idx}].expiration_date`, leg.expiration_date);
      }
    });

    navigate(`/stock?${params.toString()}`);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="alert-modal-title"
      aria-describedby="alert-modal-desc"
    >
      <Stack
        height="100%"
        alignItems="center"
        justifyContent="center"
        className="px-4"
      >
        <Box sx={{ width: "100%", maxWidth: 1000 }}>
          <Card>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 2, py: 1.5 }}
            >
              <Typography id="alert-modal-title" variant="h6" component="h2">
                Alert details
              </Typography>
              <IconButton aria-label="Close" onClick={onClose} size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Divider />
            <Box id="alert-modal-desc" sx={{ p: 2 }}>
              {!alertId ? (
                <Typography align="center">No alert selected.</Typography>
              ) : isFetching ? (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{ py: 4 }}
                >
                  <CircularProgress size={24} />
                </Stack>
              ) : !alert ? (
                <Typography align="center">Alert not found.</Typography>
              ) : (
                <Box sx={containerBorderSx}>
                  <Stack direction={{ xs: "column", sm: "row" }}>
                    <Box sx={leftColDividerSx}>
                      <Box sx={{ p: 2 }}>
                        <FormControl size="small" fullWidth>
                          <InputLabel id="strategy-select-label">
                            Select strategy
                          </InputLabel>
                          <Select
                            labelId="strategy-select-label"
                            label="Select strategy"
                            value={selectedStrategyId}
                            onChange={(e) =>
                              handleSelectStrategy(e.target.value)
                            }
                            disabled={strategiesLoading}
                          >
                            {strategiesList.map((s: Strategy) => (
                              <MenuItem key={s.id} value={s.id}>
                                {s.name} -{" "}
                                {StrategyGroupLabel[s.strategy_group]}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                      <Box sx={{ p: 2 }}>
                        <Typography sx={{ fontSize: 12, color: "#6c757d" }}>
                          Time Sent
                        </Typography>
                        <Typography sx={{ mt: 0.5, color: "#495057" }}>
                          {asText(alert.time_sent)}
                        </Typography>
                        <Box sx={{ height: 12 }} />
                        <Typography sx={{ fontSize: 12, color: "#6c757d" }}>
                          Type
                        </Typography>
                        <Box sx={{ mt: 0.75 }}>
                          <Chip
                            label="TRADE SIGNAL"
                            size="small"
                            sx={tradeTypeChipSx}
                          />
                        </Box>
                        <Box sx={{ height: 12 }} />
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography sx={{ fontSize: 12, color: "#6c757d" }}>
                            UID
                          </Typography>
                          {alert.order_id ? (
                            <Tooltip title="Copy UID">
                              <IconButton
                                size="small"
                                onClick={handleCopyUid}
                                aria-label="Copy UID"
                              >
                                <ContentCopyIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </Stack>
                        <Box sx={{ mt: 0.75 }}>
                          <Chip
                            label={asText(alert.order_id)}
                            size="small"
                            sx={grayChipSx}
                          />
                        </Box>
                        {alert.auto_extension_percentage ||
                        alert.auto_partial_close_percentage ? (
                          <Box sx={{ height: 12 }} />
                        ) : null}
                        {alert.auto_extension_percentage ? (
                          <Typography sx={{ fontSize: 12, color: "#6c757d" }}>
                            Auto Extension:
                          </Typography>
                        ) : null}
                        {alert.auto_partial_close_percentage ? (
                          <Typography sx={{ fontSize: 12, color: "#6c757d" }}>
                            Auto Partial Close:
                          </Typography>
                        ) : null}

                        <Box sx={{ mt: 0.75 }}>
                          {alert.auto_extension_percentage ? (
                            <Chip
                              label={asText(alert.auto_extension_percentage)}
                              size="small"
                              sx={grayChipSx}
                            />
                          ) : null}
                          {alert.auto_partial_close_percentage ? (
                            <Chip
                              label={asText(
                                alert.auto_partial_close_percentage,
                              )}
                              size="small"
                              sx={grayChipSx}
                            />
                          ) : null}
                        </Box>
                      </Box>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ p: 2 }}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ mb: 1 }}
                        >
                          <Typography
                            sx={{ fontSize: 12, width: 84, color: "#6c757d" }}
                          >
                            Action:
                          </Typography>
                          <Chip
                            label={contractTypeLabel}
                            size="small"
                            sx={contractTypeChipSx}
                          />
                        </Stack>

                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ mb: 1 }}
                        >
                          <Typography
                            sx={{ fontSize: 12, width: 84, color: "#6c757d" }}
                          >
                            Symbol:
                          </Typography>
                          <Chip
                            label={asText(alert.symbol)}
                            size="small"
                            sx={grayChipSx}
                          />
                        </Stack>

                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ mb: 1.5 }}
                        >
                          <Typography
                            sx={{ fontSize: 12, width: 84, color: "#6c757d" }}
                          >
                            Net Avg Price:
                          </Typography>
                          <Chip
                            label={formatMoney(alert.net_avg_price, 4)}
                            size="small"
                            sx={greenMoneyChipSx}
                          />
                        </Stack>

                        <Typography
                          sx={{ fontSize: 12, mb: 0.5, color: "#6c757d" }}
                        >
                          Legs:
                        </Typography>
                        <Stack spacing={1}>
                          {legs.length === 0 ? (
                            <Typography sx={{ p: 1, color: "#6c757d" }}>
                              No legs
                            </Typography>
                          ) : (
                            legs.map((leg, idx) => {
                              const action = (leg.action || "").toUpperCase();
                              const isBuy = action === "BUY";
                              const isSell = action === "SELL";
                              const badgeBg = isBuy
                                ? "#00ff0040"
                                : isSell
                                  ? "#fb923c"
                                  : "#e5e7eb";
                              const badgeFg = "#000000";
                              const expText = asText(leg.expiration_date);
                              const strikeFmt =
                                leg.strike_price === null ||
                                leg.strike_price === undefined
                                  ? "—"
                                  : `$${Number(leg.strike_price).toFixed(4)}`;
                              return (
                                <Stack
                                  key={`${idx}-${expText}-${strikeFmt}`}
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                  sx={legRowSx}
                                >
                                  <Chip
                                    label={action || ""}
                                    size="small"
                                    sx={{
                                      bgcolor: badgeBg,
                                      color: badgeFg,
                                      fontWeight: 700,
                                      fontSize: 12,
                                    }}
                                  />
                                  <Typography sx={{ color: "#6c757d" }}>
                                    {expText}
                                  </Typography>
                                  <Typography sx={{ color: "#6c757d" }}>
                                    @
                                  </Typography>
                                  <Typography sx={{ color: "#495057" }}>
                                    {strikeFmt}
                                  </Typography>
                                  <Button
                                    variant="contained"
                                    onClick={() => handleRedirect([leg])}
                                    disabled={isButtonDisabled}
                                    sx={{ mt: 1, marginLeft: "auto" }}
                                  >
                                    Redirect
                                  </Button>
                                </Stack>
                              );
                            })
                          )}
                        </Stack>
                      </Box>
                    </Box>
                  </Stack>
                </Box>
              )}
            </Box>
          </Card>
        </Box>
      </Stack>
    </Modal>
  );
};
